import { createEnum } from "@redotech/zod/zod-enum";
import * as z from "zod/v4";

export enum ElementVisibility {
  ALL = "all",
  DESKTOP_ONLY = "desktop-only",
  MOBILE_ONLY = "mobile-only",
}

const baseElementSchema = z.object({
  _id: z.string().optional(),
  visibility: createEnum(ElementVisibility, {
    enumName: "ElementVisibility",
  }).optional(),
  backgroundColor: z.string().optional(),
  borderColor: z.string().optional(),
  borderWidth: z.number().optional(),
  cornerRadius: z.number().optional(),
  padding: z
    .object({
      top: z.number(),
      right: z.number(),
      bottom: z.number(),
      left: z.number(),
    })
    .optional(),
});
export type BaseElement = z.infer<typeof baseElementSchema>;

export enum MarketingForgeElementType {
  RICH_TEXT = "rich-text",
  BUTTON = "button",
  INPUT = "input",
  IMAGE = "image",
  PRODUCT = "product",
  COLUMN = "column",
  CONTAINER = "container",
  DISCOUNT = "discount",
  TEXT_TO_SUBSCRIBE_BUTTON = "text-to-subscribe-button",
  TEXT_TO_SUBSCRIBE_DESCRIPTION = "text-to-subscribe-description",
}

export enum ButtonAction {
  SUBMIT_AND_ADVANCE = "submit-and-advance",
  SUBMIT_AND_GO_TO_URL = "submit-and-go-to-url",
  CLOSE = "close",
}

// Button interaction types for Spotlights
export enum ButtonInteractionType {
  GO_TO_NEXT = "go-to-next",
  GO_TO_STEP = "go-to-step",
  CLOSE = "close",
  CLOSE_AND_ACTION = "close-and-action",
  OPEN_LINK = "open-link",
}

// Close action types (for close-and-action interaction)
export enum CloseActionType {
  ADD_DISCOUNT_TO_CART = "add-discount-to-cart",
  OPEN_CART = "open-cart",
  OPEN_CHECKOUT = "open-checkout",
  ADD_PRODUCT_TO_CART = "add-product-to-cart",
}

// Close action schemas
const addDiscountToCartActionSchema_ = z.object({
  type: z.literal(CloseActionType.ADD_DISCOUNT_TO_CART),
  discountId: z.string(),
});
export interface AddDiscountToCartAction
  extends z.infer<typeof addDiscountToCartActionSchema_> {}

const openCartActionSchema_ = z.object({
  type: z.literal(CloseActionType.OPEN_CART),
});
export interface OpenCartAction extends z.infer<typeof openCartActionSchema_> {}

const openCheckoutActionSchema_ = z.object({
  type: z.literal(CloseActionType.OPEN_CHECKOUT),
});
export interface OpenCheckoutAction
  extends z.infer<typeof openCheckoutActionSchema_> {}

const addProductToCartActionSchema_ = z.object({
  type: z.literal(CloseActionType.ADD_PRODUCT_TO_CART),
  productId: z.string().optional(),
  variantId: z.string().optional(),
  quantity: z.number().optional(),
  productTitle: z.string().optional(),
  productImageUrl: z.string().optional(),
});
export interface AddProductToCartAction
  extends z.infer<typeof addProductToCartActionSchema_> {}

const closeActionSchema_ = z.discriminatedUnion("type", [
  addDiscountToCartActionSchema_,
  openCartActionSchema_,
  openCheckoutActionSchema_,
  addProductToCartActionSchema_,
]);
export const CloseActionSchema: z.ZodType<CloseAction> = closeActionSchema_;
export type CloseAction = z.infer<typeof closeActionSchema_>;

// Button interaction schemas
const goToNextInteractionSchema_ = z.object({
  interactionType: z.literal(ButtonInteractionType.GO_TO_NEXT),
});
export interface GoToNextInteraction
  extends z.infer<typeof goToNextInteractionSchema_> {}

const goToStepInteractionSchema_ = z.object({
  interactionType: z.literal(ButtonInteractionType.GO_TO_STEP),
  stepId: z.string(),
});
export interface GoToStepInteraction
  extends z.infer<typeof goToStepInteractionSchema_> {}

const closeInteractionSchema_ = z.object({
  interactionType: z.literal(ButtonInteractionType.CLOSE),
});
export interface CloseInteraction
  extends z.infer<typeof closeInteractionSchema_> {}

const closeAndActionInteractionSchema_ = z.object({
  interactionType: z.literal(ButtonInteractionType.CLOSE_AND_ACTION),
  actions: z.array(closeActionSchema_),
});
export interface CloseAndActionInteraction
  extends z.infer<typeof closeAndActionInteractionSchema_> {}

const openLinkInteractionSchema_ = z.object({
  interactionType: z.literal(ButtonInteractionType.OPEN_LINK),
  url: z.string(),
  openInNewTab: z.boolean(),
});
export interface OpenLinkInteraction
  extends z.infer<typeof openLinkInteractionSchema_> {}

const buttonInteractionSchema_ = z.discriminatedUnion("interactionType", [
  goToNextInteractionSchema_,
  goToStepInteractionSchema_,
  closeInteractionSchema_,
  closeAndActionInteractionSchema_,
  openLinkInteractionSchema_,
]);
export const ButtonInteractionSchema: z.ZodType<ButtonInteraction> =
  buttonInteractionSchema_;
export type ButtonInteraction = z.infer<typeof buttonInteractionSchema_>;

export enum CustomFieldType {
  Date = "date",
  Dropdown = "dropdown",
  Number = "number",
  TextLong = "text-long",
  TextShort = "text-short",
  YesNoQuestion = "yes-no-question",
}

export const fontWeights = ["regular", "semibold", "bold"] as const;
export type FontWeight = (typeof fontWeights)[number];

export const buttonHierarchies = ["primary", "secondary", "tertiary"] as const;
export type ButtonHierarchy = (typeof buttonHierarchies)[number];

const buttonStyleOverrideSchema_ = z.object({
  backgroundColor: z.string(),
  textColor: z.string(),
  border: z.object({
    cornerRadiusPx: z.number(),
    stroke: z.object({ color: z.string(), weightPx: z.number() }),
  }),
  padding: z.object({
    top: z.number(),
    right: z.number(),
    bottom: z.number(),
    left: z.number(),
  }),
  font: z.object({
    fontFamily: z.string(),
    fontSizePx: z.number(),
    fontWeight: createEnum(fontWeights, { enumName: "FontWeight" }),
  }),
});
export const ButtonStyleOverrideSchema: z.ZodType<ButtonStyleOverride> =
  buttonStyleOverrideSchema_;
export interface ButtonStyleOverride
  extends z.infer<typeof buttonStyleOverrideSchema_> {}

export const inputTypes = [
  "email",
  "phone",
  "birthday",
  "firstName",
  "lastName",
  "fullName",
  "custom",
] as const;
export type InputType = (typeof inputTypes)[number];

export enum FontFamily {
  ARIAL = "Arial",
  COURIER_NEW = "Courier New",
  GEORGIA = "Georgia",
  LUCIDA_SANS_UNICODE = "Lucida Sans Unicode",
  TAHOMA = "Tahoma",
  TIMES_NEW_ROMAN = "Times New Roman",
  TREBUCHET_MS = "Trebuchet MS",
  VERDANA = "Verdana",
}

export const richTextElementSchema = baseElementSchema.extend({
  /** @deprecated use the baseElementSchema._id instead */
  id: z.union([z.number(), z.string()]),
  type: z.literal(MarketingForgeElementType.RICH_TEXT),
  html: z.string(),
  fontFamily: z.string(),
  fontWeight: createEnum(fontWeights, { enumName: "FontWeight" }),
  fontSizePx: z.number(),
  textColor: z.string(),
  linkColor: z.string(),
});
export const RichTextElementSchema: z.ZodType<RichTextElement> =
  richTextElementSchema;
export type RichTextElement = z.infer<typeof richTextElementSchema>;

const buttonElementSchema = baseElementSchema.extend({
  type: z.literal(MarketingForgeElementType.BUTTON),
  hierarchy: createEnum(buttonHierarchies, { enumName: "ButtonHierarchy" }),
  text: z.string(),
  action: createEnum(ButtonAction, { enumName: "ButtonAction" }),
  destinationUrl: z.string().optional(),
  interaction: buttonInteractionSchema_.optional(),
  styleOverride: buttonStyleOverrideSchema_.nullish(),
});
export const ButtonElementSchema: z.ZodType<ButtonElement> =
  buttonElementSchema;
export type ButtonElement = z.infer<typeof buttonElementSchema>;

const inputElementSchema = baseElementSchema.extend({
  type: z.literal(MarketingForgeElementType.INPUT),
  inputType: createEnum(inputTypes, { enumName: "InputTypes" }),
  placeholderText: z.string(),
  customField: z
    .object({
      id: z.string(),
      fieldType: createEnum(CustomFieldType, { enumName: "CustomFieldType" }),
    })
    .optional(),
  optional: z.boolean().optional(),
});
export const InputElementSchema: z.ZodType<InputElement> = inputElementSchema;
export type InputElement = z.infer<typeof inputElementSchema>;

export const imageElementSchema = baseElementSchema.extend({
  type: z.literal(MarketingForgeElementType.IMAGE),
  url: z.string(),
});
export const ImageElementSchema: z.ZodType<ImageElement> = imageElementSchema;
export type ImageElement = z.infer<typeof imageElementSchema>;

export enum ProductLayout {
  VERTICAL = "vertical",
  HORIZONTAL = "horizontal",
}

export const productElementSchema = baseElementSchema.extend({
  type: z.literal(MarketingForgeElementType.PRODUCT),
  productId: z.string().optional(),
  productTitle: z.string().optional(),
  productImageUrl: z.string().optional(),
  productPrice: z.string().optional(),
  productCompareAtPrice: z.string().optional(),
  productLayout: createEnum(ProductLayout, { enumName: "ProductLayout" }),
  fontFamily: z.string().optional(),
  fontSize: z.number().optional(),
});
export const ProductElementSchema: z.ZodType<ProductElement> =
  productElementSchema;
export type ProductElement = z.infer<typeof productElementSchema>;

export enum Alignment {
  LEFT = "left",
  CENTER = "center",
  RIGHT = "right",
}

export const alignmentToFlexAlign: Record<Alignment, string> = {
  [Alignment.LEFT]: "flex-start",
  [Alignment.CENTER]: "center",
  [Alignment.RIGHT]: "flex-end",
};

const discountCodeElementSchema = baseElementSchema.extend({
  type: z.literal(MarketingForgeElementType.DISCOUNT),
  discountId: z.string().optional(),
  fontFamily: z.string(),
  fontWeight: createEnum(fontWeights, { enumName: "FontWeight" }),
  fontSize: z.number(),
  textColor: z.string(),
  blockBackgroundColor: z.string(),
  /** @deprecated use baseElementSchema.backgroundColor instead */
  sectionColor: z.string(),
  /** @deprecated use baseElementSchema.padding instead */
  sectionPadding: z.object({
    top: z.number(),
    right: z.number(),
    bottom: z.number(),
    left: z.number(),
  }),
  alignment: createEnum(Alignment, { enumName: "Alignment" }),
  borderRadius: z.number(),
});
export const DiscountCodeElementSchema: z.ZodType<DiscountCodeElement> =
  discountCodeElementSchema;
export type DiscountCodeElement = z.infer<typeof discountCodeElementSchema>;

const textToSubscribeButtonElementSchema = buttonElementSchema.extend({
  type: z.literal(MarketingForgeElementType.TEXT_TO_SUBSCRIBE_BUTTON),
});
export const TextToSubscribeButtonElementSchema: z.ZodType<TextToSubscribeButtonElement> =
  textToSubscribeButtonElementSchema;
export type TextToSubscribeButtonElement = z.infer<
  typeof textToSubscribeButtonElementSchema
>;

const textToSubscribeDescriptionElementSchema = baseElementSchema.extend({
  type: z.literal(MarketingForgeElementType.TEXT_TO_SUBSCRIBE_DESCRIPTION),
  fontFamily: z.string(),
  textColor: z.string(),
});
export const TextToSubscribeDescriptionElementSchema: z.ZodType<TextToSubscribeDescriptionElement> =
  textToSubscribeDescriptionElementSchema;
export type TextToSubscribeDescriptionElement = z.infer<
  typeof textToSubscribeDescriptionElementSchema
>;

const ElementUnion = z.union([
  richTextElementSchema,
  buttonElementSchema,
  inputElementSchema,
  imageElementSchema,
  productElementSchema,
  discountCodeElementSchema,
  textToSubscribeButtonElementSchema,
  textToSubscribeDescriptionElementSchema,
]);

const columnElementSchema = baseElementSchema.extend({
  type: z.literal(MarketingForgeElementType.COLUMN),
  elements: z.array(z.lazy(() => ElementUnion)),
  totalColumns: z.number(),
  columnWidths: z.array(z.number()).optional(),
});
export const ColumnElementSchema: z.ZodType<ColumnElement> =
  columnElementSchema;
export type ColumnElement = z.infer<typeof columnElementSchema>;

export const containerDirections = ["vertical", "horizontal"] as const;
export type ContainerDirection = (typeof containerDirections)[number];

type ContainerElement = BaseElement & {
  type: MarketingForgeElementType.CONTAINER;
  direction: ContainerDirection;
  elements: (
    | RichTextElement
    | ButtonElement
    | InputElement
    | ImageElement
    | ProductElement
    | ColumnElement
    | DiscountCodeElement
    | TextToSubscribeButtonElement
    | TextToSubscribeDescriptionElement
    | ContainerElement
  )[];
};

const containerElementSchema: z.ZodType<ContainerElement> = z.lazy(() =>
  baseElementSchema.extend({
    type: z.literal(MarketingForgeElementType.CONTAINER),
    direction: createEnum(containerDirections, {
      enumName: "ContainerDirection",
    }),
    elements: z.array(
      z.union([
        richTextElementSchema,
        buttonElementSchema,
        inputElementSchema,
        imageElementSchema,
        productElementSchema,
        columnElementSchema,
        discountCodeElementSchema,
        textToSubscribeButtonElementSchema,
        textToSubscribeDescriptionElementSchema,
        containerElementSchema,
      ]),
    ),
  }),
);

export const ContainerElementSchema: z.ZodType<ContainerElement> =
  containerElementSchema;
export type { ContainerElement };

const FormElementUnion = z.union([
  richTextElementSchema,
  buttonElementSchema,
  inputElementSchema,
  imageElementSchema,
  productElementSchema,
  columnElementSchema,
  discountCodeElementSchema,
  textToSubscribeButtonElementSchema,
  textToSubscribeDescriptionElementSchema,
  containerElementSchema,
]);

export type FormElement = z.infer<typeof FormElementUnion>;
